<template>
    <div class="product-overview">
        <h2 class="title">{{ blok.Title }}</h2>
        <div class="distance">
            <div v-if="blok.TransferTime" class="transfer">
                <NuxtIcon name="ri:bus-fill" class="h-4 inline-block mb-1" />
                {{ blok.TransferTime }}
            </div>
            <div v-if="blok.Distance || blok.Distance === 0" class="from-airport">
                <NuxtIcon name="ri:map-pin-fill" class="h-4 inline-block mb-1" />
                {{ `${blok.Distance} miles to ${blok.AirportCode}` }}
            </div>
        </div>
        <div class="product-container">
            <div class="product-image">
                <NuxtImg
                    v-if="blok.ProductImage?.filename"
                    :src="blok.ProductImage.filename"
                    :alt="blok.Title"
                    density="x1"
                    format="webp"
                    loading="eager"
                />

                <!-- Feefo section -->
                <div v-if="blok.ProductId" class="reviews">
                    <!-- SSR content for crawlers - always present, always hidden visually -->
                    <FeefoServiceReviewsSsr :product-sku="feefoConfig.productSKU" :limit="5" :visually-hidden="true" />

                    <!-- Client-side widget -->
                    <ClientOnly>
                        <div
                            id="feefologohere"
                            :[feefoConfig.attributeType]="feefoConfig.productSKU"
                            ref="feefoContainer"
                        ></div>
                    </ClientOnly>
                </div>
            </div>
            <div class="benefits">
                <ul>
                    <template v-for="benefit in blok.Benefits" :key="benefit._uid">
                        <li><richText :content="benefit.content.content" /></li>
                    </template>
                </ul>

                <template v-for="award in blok.Award" :key="award._uid">
                    <AwardBadge :blok="award" />
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import FeefoServiceReviewsSsr from '@nuxt-monorepo/ui/components/Reviews/FeefoServiceReviewsSsr.vue';

const { blok } = defineProps<{ blok: any }>();

const { render } = useStoryblokRichText({});
const richText = (content: any) => {
    return render(content.content);
};

const feefoContainer = ref<HTMLDivElement>(null);

/*
Feefo library creates button in shadow root with aria-labelledby attribute which is referring to a non-existent element.
To solve this issue span element with correct ID is added after script loading to the shadow root
*/
const feefoAccessibilityFix = async (callCount = 0, maxCalls = 150) => {
    const wrapper = feefoContainer.value?.children[0];
    if (!wrapper) {
        if (callCount >= maxCalls) return; // limit the number of recursive calls
        setTimeout(() => feefoAccessibilityFix(callCount + 1, maxCalls), 100); // recursive call to wait for content creation by library
        return;
    }
    wrapper.shadowRoot.appendChild(
        Object.assign(document.createElement('span'), {
            id: 'pop-up-reviews',
            textContent: 'Feefo popup button',
            hidden: true,
        })
    );
};

// Function to handle events and load script
const initFeefo = () => {
    let isLoading = false;
    const events = ['mousemove', 'scroll', 'keydown', 'click', 'wheel'];

    const loadHandler = async () => {
        // Prevent multiple loads
        if (isLoading) return;
        isLoading = true;

        try {
            // Remove all event listeners
            events.forEach((event) => {
                document.removeEventListener(event, loadHandler);
            });

            // Load the script
            await loadFeefoScript();
            console.log('Script loaded successfully');
            feefoAccessibilityFix();
        } catch (error) {
            console.error('Failed to load script:', error);
            isLoading = false; // Reset flag in case of error
        }
    };

    // Add event listeners
    events.forEach((event) => {
        document.addEventListener(event, loadHandler, { passive: true });
    });
};

const loadFeefoScript = () => {
    // Create a promise to handle script loading
    return new Promise((resolve, reject) => {
        // Create script element
        const script = document.createElement('script');
        script.src = 'https://api.feefo.com/api/javascript/skypark-secure';
        script.async = true;

        // Add load and error handlers
        script.onload = () => resolve(script);
        script.onerror = () =>
            reject(new Error(`Script load error: https://api.feefo.com/api/javascript/skypark-secure`));

        // Add to document
        document.head.appendChild(script);
    });
};

onMounted(() => {
    initFeefo();
});

const propelProductSKU = inject<string>('propelProductSKU', '');
const feefoConfig = computed(() => ({
    productSKU: propelProductSKU ? propelProductSKU : blok.ProductId,
    attributeType: propelProductSKU ? 'data-parent-product-sku' : 'data-product-sku',
}));
</script>

<style scoped lang="postcss">
.product-overview {
    @apply p-4 pr-2;
    .title {
        @apply text-2xl font-semibold text-primary;
    }
    .distance {
        @apply flex items-center text-gray-800 font-semibold py-2;
        .transfer,
        .from-airport {
            @apply flex items-center pr-8;
            .iconify {
                @apply h-5 w-5 mr-2;
            }
        }
    }
    .product-container {
        @apply flex flex-col md:flex-row;
        .product-image {
            @apply flex flex-wrap flex-col sm:flex-row md:flex-col;
            img {
                @apply inline-block min-w-[225px] max-w-[225px] mr-4 mb-4 object-contain;
            }
        }
        .benefits {
            @apply inline-block;
            ul {
                @apply list-none pl-6;
                li {
                    @apply relative py-1;
                    :after {
                        @apply content-[url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MTIgNTEyIj48IS0tIUZvbnQgQXdlc29tZSBQcm8gNi42LjAgYnkgQGZvbnRhd2Vzb21lIC0gaHR0cHM6Ly9mb250YXdlc29tZS5jb20gTGljZW5zZSAtIGh0dHBzOi8vZm9udGF3ZXNvbWUuY29tL2xpY2Vuc2UgKENvbW1lcmNpYWwgTGljZW5zZSkgQ29weXJpZ2h0IDIwMjQgRm9udGljb25zLCBJbmMuLS0+PHBhdGggZmlsbD0iI2Y2YWIzZCIgZD0iTTAgMjU2YTI1NiAyNTYgMCAxIDAgNTEyIDBBMjU2IDI1NiAwIDEgMCAwIDI1NnptMTM2IDBjMC02LjEgMi4zLTEyLjMgNy0xN2M5LjQtOS40IDI0LjYtOS40IDMzLjkgMGw0NyA0N2MzNy0zNyA3NC03NCAxMTEtMTExYzQuNy00LjcgMTAuOC03IDE3LTdzMTIuMyAyLjMgMTcgN2MyLjMgMi4zIDQuMSA1IDUuMyA3LjljLjYgMS41IDEgMi45IDEuMyA0LjRjLjIgMS4xIC4zIDIuMiAuMyAyLjJjLjEgMS4yIC4xIDEuMiAuMSAyLjVjLS4xIDEuNS0uMSAxLjktLjEgMi4zYy0uMSAuNy0uMiAxLjUtLjMgMi4yYy0uMyAxLjUtLjcgMy0xLjMgNC40Yy0xLjIgMi45LTIuOSA1LjYtNS4zIDcuOWMtNDIuNyA0Mi43LTg1LjMgODUuMy0xMjggMTI4Yy00LjcgNC43LTEwLjggNy0xNyA3cy0xMi4zLTIuMy0xNy03Yy0yMS4zLTIxLjMtNDIuNy00Mi43LTY0LTY0Yy00LjctNC43LTctMTAuOC03LTE3eiIvPjxwYXRoIGZpbGw9IndoaXRlIiBkPSJNMzY5IDE3NWM5LjQgOS40IDkuNCAyNC42IDAgMzMuOUwyNDEgMzM3Yy05LjQgOS40LTI0LjYgOS40LTMzLjkgMGwtNjQtNjRjLTkuNC05LjQtOS40LTI0LjYgMC0zMy45czI0LjYtOS40IDMzLjkgMGw0NyA0N0wzMzUgMTc1YzkuNC05LjQgMjQuNi05LjQgMzMuOSAweiIvPjwvc3ZnPg==")];
                        @apply block h-4 w-4 absolute text-[#f6ab3d] -left-6 top-2 text-sm;
                    }
                }
            }
        }
    }
}
</style>
